import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { getEmpresaAtual } from '../../api';
import {
  coresHex, hexParaRgb,
} from '../../../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

export const generateLeadsReport = async (dashboardData, filterInfo, chartsToInclude) => {
  const pdfDoc = await PDFDocument.create();
  const fontReg = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const W = 595.28; const H = 841.89;
  const ML = 40; const MR = 40;
  const CW = W - ML - MR;

  const C = {
    cinzaChumbo: rgbHex(coresHex.chumbo),
    cinzaMedio: rgbHex(coresHex.cinzaMedio),
    cinzaClaro: rgbHex(coresHex.cinzaClaro),
    laranja: rgbHex(coresHex.laranja),
    laranjaClaro: rgbHex(coresHex.laranjaClaro),
    branco: rgb(1, 1, 1),
    preto: rgb(0.133, 0.133, 0.133),
    borda: rgbHex(coresHex.borda),
  };

  const drawRect = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width: w, height: h, color });
  const drawBRect = (pg, x, y, w, h, fill, border) => pg.drawRectangle({
    x, y, width: w, height: h, color: fill, borderColor: border || C.borda, borderWidth: 0.5,
  });
  const drawText = (pg, t, x, y, s, f, color) => pg.drawText(String(t ?? ''), {
    x, y, size: s, font: f, color,
  });
  const drawLine = (pg, x1, y1, x2, y2, color = C.borda, t = 0.5) => pg.drawLine({
    start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness: t, color,
  });

  const tH = 16;
  const drawRow = (pg, cells, y) => {
    cells.forEach((c) => {
      drawBRect(pg, c.x, y - tH + 2, c.w, tH, c.fill, c.bc || C.borda);
      if (c.text != null && c.text !== '') {
        const sz = c.sz || 9; const fn = c.fn || fontReg;
        let tx = c.x + 6;
        if (c.al === 'r') tx = c.x + c.w - fn.widthOfTextAtSize(String(c.text), sz) - 6;
        if (c.al === 'c') tx = c.x + (c.w - fn.widthOfTextAtSize(String(c.text), sz)) / 2;
        drawText(pg, c.text, tx, y - tH + 6, sz, fn, c.cl || C.preto);
      }
    });
    return y - tH;
  };

  // ── Logo (mesma lógica dos demais relatórios do sistema) ──────────────────
  let logoImage = null;
  let empresa = null;
  try {
    empresa = await getEmpresaAtual();
    if (empresa?.logoBranca) {
      const base64 = empresa.logoBranca.split(',')[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      logoImage = await pdfDoc.embedPng(bytes);
    }
  } catch (e) {
    // sem logo — segue sem quebrar o relatório
  }

  const periodo = [filterInfo.dataInicio, filterInfo.dataFim].filter(Boolean).join(' até ');

  // ── Cabeçalho (barra chumbo + faixa laranja) — evita a sobreposição da
  // logo com o texto de filtros que existia na versão anterior, já que
  // logo e título agora ficam confinados a uma barra de altura fixa. ──────
  const drawHeader = (pg, titulo) => {
    drawRect(pg, 0, H - 44, W, 44, C.cinzaChumbo);
    drawRect(pg, 0, H - 44, 6, 44, C.laranja);
    if (logoImage) {
      const d = logoImage.scaleToFit(70, 28);
      pg.drawImage(logoImage, { x: ML, y: H - 39, width: d.width, height: d.height });
    } else {
      drawText(pg, empresa?.nome || 'Marcki Engenharia', ML, H - 26, 11, fontBold, C.branco);
    }
    drawText(pg, titulo, W - MR - fontBold.widthOfTextAtSize(titulo, 10), H - 24, 10, fontBold, C.branco);
    if (periodo) {
      drawText(pg, `Período: ${periodo}`, W - MR - fontReg.widthOfTextAtSize(`Período: ${periodo}`, 8), H - 36, 8, fontReg, rgb(0.8, 0.8, 0.8));
    }
    return H - 58;
  };

  const drawFooter = (pg) => {
    drawRect(pg, 0, 0, W, 26, C.cinzaChumbo);
    drawRect(pg, 0, 0, 6, 26, C.laranja);
    drawText(pg, `Solicitante: ${filterInfo.geradoPor || '—'}`, ML + 8, 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
    const ger = `Gerado em ${filterInfo.dataGeracao || '--'}`;
    drawText(pg, ger, W - MR - fontReg.widthOfTextAtSize(ger, 7), 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
  };

  const report = dashboardData.report || {};

  // ============================================================
  // PG 1 — CABEÇALHO + KPIs + DADOS GERAIS
  // ============================================================
  let page = pdfDoc.addPage([W, H]);
  let y = drawHeader(page, 'Relatório de Leads');
  y -= 10;

  if (periodo) { drawText(page, `De ${periodo}`, ML, y, 8, fontReg, C.cinzaMedio); y -= 12; }
  const filtroLinha = `Consultor: ${filterInfo.consultor || 'Todos'}   |   Unidade: ${filterInfo.unidade || 'Todas'}`;
  drawText(page, filtroLinha, ML, y, 8, fontReg, C.laranja);
  y -= 20;

  // KPIs de destaque
  const kpiW = (CW - 15) / 4;
  const kpiH = 36;
  const kpis = [
    { label: 'Total de Leads', valor: String(report.totalLeads || 0) },
    { label: 'Propostas Realizadas', valor: String(report.propostasRealizadas || 0) },
    { label: 'Vendas Realizadas', valor: String(report.vendasRealizadas || 0) },
    { label: 'Taxa de Conversão', valor: report.taxaConversao || '0%' },
  ];
  kpis.forEach((kpi, i) => {
    const x = ML + i * (kpiW + 5);
    drawBRect(page, x, y - kpiH, kpiW, kpiH, C.laranjaClaro, C.laranja);
    drawRect(page, x, y - kpiH, 3, kpiH, C.laranja);
    drawText(page, kpi.label, x + 8, y - 13, 7, fontReg, C.cinzaMedio);
    drawText(page, kpi.valor, x + 8, y - 28, 12, fontBold, C.cinzaChumbo);
  });
  y -= kpiH + 20;

  // Seção 1 — Dados gerais (tabela no padrão dos demais relatórios)
  drawRect(page, ML, y + 2, CW, 16, C.cinzaChumbo);
  drawText(page, '1. DADOS GERAIS', ML + 6, y + 7, 8, fontBold, C.branco);
  y -= 16;

  const c1 = CW * 0.6; const c2 = CW - c1;
  y = drawRow(page, [
    { x: ML, w: c1, fill: C.laranja, text: 'Informação', fn: fontBold, cl: C.branco },
    { x: ML + c1, w: c2, fill: C.laranja, text: 'Valor', fn: fontBold, cl: C.branco, al: 'r' },
  ], y);

  const linhas = [
    ['Leads Qualificados:', report.leadsQualificados || 0],
    ['Leads Desqualificados:', report.leadsDesqualificados || 0],
    ['Total em Negociação:', report.totalEmNegociacao || 'R$ 0,00'],
    ['Potência Total:', report.potenciaTotal || '0 kWp'],
    ['Total em Vendas:', report.totalEmVendas || 'R$ 0,00'],
    ['Potência Vendida:', report.potenciaVendida || '0 kWp'],
    ['Propostas Perdidas:', report.propostasPerdidas || 0],
    ['Taxa de Perdidos:', report.taxaPerdidos || '0%'],
  ];
  linhas.forEach(([label, valor], i) => {
    const fill = i % 2 === 0 ? C.cinzaClaro : C.branco;
    y = drawRow(page, [
      { x: ML, w: c1, fill, text: label },
      { x: ML + c1, w: c2, fill, text: String(valor), al: 'r' },
    ], y);
  });

  // Seção 2 — Análise gráfica
  y -= 20;
  if (chartsToInclude && chartsToInclude.length > 0) {
    drawRect(page, ML, y + 2, CW, 16, C.cinzaChumbo);
    drawText(page, '2. ANÁLISE GRÁFICA', ML + 6, y + 7, 8, fontBold, C.branco);
    y -= 24;

    let idx = 0;
    // eslint-disable-next-line no-await-in-loop
    for (let i = 0; i < chartsToInclude.length; i += 1) {
      const chartId = chartsToInclude[i];
      if (typeof window !== 'undefined' && window.ApexCharts) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await window.ApexCharts.exec(chartId, 'dataURI');
          if (res && res.imgURI) {
            // eslint-disable-next-line no-await-in-loop
            const pngImageBytes = await fetch(res.imgURI).then((r) => r.arrayBuffer());
            // eslint-disable-next-line no-await-in-loop
            const pngImage = await pdfDoc.embedPng(pngImageBytes);

            // Layout: 2 gráficos por linha
            const scaleParams = pngImage.scale(0.48);
            let imgW = scaleParams.width;
            let imgH = scaleParams.height;
            if (imgW > 240) {
              imgW = 240;
              imgH = (imgW / scaleParams.width) * scaleParams.height;
            }

            const isEven = idx % 2 === 0;
            const xPos = isEven ? ML : ML + CW / 2 + 10;
            if (isEven && idx > 0) {
              y -= (imgH + 45);
            }
            if (y - imgH < 50) {
              drawFooter(page);
              page = pdfDoc.addPage([W, H]);
              y = drawHeader(page, 'Relatório de Leads') - 20;
            }

            page.drawImage(pngImage, {
              x: xPos,
              y: y - imgH,
              width: imgW,
              height: imgH,
            });

            idx += 1;
          }
        } catch (error) {
          console.error(`Error getting chart ${chartId}`, error);
        }
      }
    }
    y -= 10;
  }

  drawLine(page, ML, y, W - MR, y, C.laranja, 0.5);

  // Rodapé em todas as páginas
  const pages = pdfDoc.getPages();
  pages.forEach((p) => drawFooter(p));

  // Save PDF
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Relatorio_Leads_${new Date().getTime()}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};